import { useCallback, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { RIDE_STATES } from '@rideiq/constants';
import { useTimezone } from '@rideiq/hooks/useTimezone';
import { useWebSocket } from '@rideiq/hooks/useWebSocket';
import { useQueryClient } from '@tanstack/react-query';
import { addMinutes } from 'date-fns';
import { includes } from 'lodash';

import { RIDE_QUERY_KEY, useRideQuery } from './useRideQuery';

const EVENTS_TOPIC = 'events';

type Message = {
  topic: string;
  data: { eventName: string; eventAttributes: string }[];
};

// Mount once per ride page; query observers share cached data without opening sockets.
export const useRideLiveUpdates = ({ rideDetails, setRideDetails, serializeRide }: ReturnType<typeof useRideQuery>) => {
  const { rideId } = useParams() as { rideId: string };
  const isEditView = !!window.location.pathname.match(/(new|edit)/);
  const cacheKey = rideId || window.location.pathname;
  const queryClient = useQueryClient();
  const { getDateInTimezone } = useTimezone();

  const shouldGetLiveUpdated = !!(
    includes([...RIDE_STATES.upcoming, ...RIDE_STATES.inProgress], rideDetails?.status) &&
    !isEditView &&
    rideDetails?.id
  );

  const { lastJsonMessage: rideStateChanged } = useWebSocket<Message>(
    {
      topic: EVENTS_TOPIC,
      resource_uuid: !isEditView && rideDetails?.uuid ? rideDetails.uuid : '',
      resource_type: 'trip',
    },
    { reconnectAttempts: 10 },
    shouldGetLiveUpdated
  );

  const handleRouteLegETAChanged = useCallback(
    (routeLeg) => {
      setRideDetails((current) => {
        if (!current) {
          return current;
        }

        const waypoints = current.waypoints.map((wp) => ({
          ...wp,
          ...(wp.id === routeLeg.endWaypointId && {
            estimatedArrivesAt: getDateInTimezone(routeLeg.liveExpectedEndsAt),
            estimatedDepartsAt: addMinutes(getDateInTimezone(routeLeg.liveExpectedEndsAt), 5),
          }),
        }));

        return serializeRide({ ...current, waypoints }, current);
      });
    },
    [getDateInTimezone, serializeRide, setRideDetails]
  );

  useEffect(() => {
    if (rideStateChanged && rideStateChanged.topic === EVENTS_TOPIC) {
      rideStateChanged.data.forEach((data) => {
        if (data.eventName === 'state_changed' || data.eventName === 'starts_at_changed') {
          queryClient.invalidateQueries({ queryKey: [RIDE_QUERY_KEY, cacheKey] });
        }

        if (data.eventName === 'route_leg_eta_changed') {
          const routeLeg: RideDetails = JSON.parse(data.eventAttributes).route_leg;

          handleRouteLegETAChanged(routeLeg);
        }
      });
    }
  }, [rideStateChanged, queryClient, cacheKey, handleRouteLegETAChanged]);
};
